// Public Storefront API credentials are safe to include in this browser bundle.
// Do not use an Admin API token or a private Storefront token here.
const SHOPIFY_STORE_DOMAIN = "meama-vending.myshopify.com";
const SHOPIFY_STOREFRONT_PUBLIC_ACCESS_TOKEN = "eb424146583fe8c7949840dc7f2b7a8a";
const SHOPIFY_STOREFRONT_API_VERSION = "2026-07";

const CAMPAIGN_METAOBJECT_TYPE = "dropper_campaign_popup";
const CAMPAIGNS_PER_PAGE = 250;

const CAMPAIGN_POPUPS_QUERY = `
  query CampaignPopups($type: String!, $first: Int!, $after: String) {
    metaobjects(type: $type, first: $first, after: $after) {
      nodes {
        id
        fields {
          key
          type
          value
          reference {
            __typename
            ... on MediaImage {
              image {
                url
                altText
                width
                height
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const normalizeValue = (value) => value?.trim().toLowerCase() ?? "";

const richTextToPlainText = (value) => {
  try {
    const content = JSON.parse(value);
    const textParts = [];

    const visit = (node) => {
      if (typeof node?.value === "string") {
        textParts.push(node.value);
      }

      node?.children?.forEach(visit);
    };

    visit(content);
    return textParts.join(" ").trim();
  } catch {
    return value;
  }
};

const getTextValue = (field) => {
  const value = field?.value?.trim() ?? "";
  return field?.type === "rich_text_field" ? richTextToPlainText(value) : value;
};

const getBooleanValue = (field) => (
  field?.type === "boolean" && field.value === "true"
);

const getSafeUrl = (value) => {
  if (!value) return "";

  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) ? url.toString() : "";
  } catch {
    return "";
  }
};

const mapMetaobjectToCampaign = (metaobject) => {
  const fields = Object.fromEntries(
    (metaobject?.fields ?? []).map((field) => [field.key, field])
  );
  const image = fields.image?.reference?.image;

  return {
    id: metaobject?.id,
    title: getTextValue(fields.title),
    description: getTextValue(fields.description),
    channel: getTextValue(fields.channel),
    activeStatus: getBooleanValue(fields.active_status),
    image: image?.url ? {
      url: image.url,
      altText: image.altText ?? "",
      width: image.width,
      height: image.height,
    } : null,
    buttonUrl: getSafeUrl(getTextValue(fields.button_url)),
    buttonTitle: getTextValue(fields.button_title),
  };
};

export const selectActiveGuestCampaign = (metaobjects) => {
  const activeGuestCampaigns = (metaobjects ?? [])
    .map(mapMetaobjectToCampaign)
    .filter((campaign) => (
      normalizeValue(campaign.channel) === "guest" &&
      campaign.activeStatus
    ));

  if (activeGuestCampaigns.length !== 1) return null;

  const campaign = activeGuestCampaigns[0];
  if (!campaign.title) return null;

  return campaign;
};

const getShopifyEndpoint = () => {
  const storeDomain = SHOPIFY_STORE_DOMAIN
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "");

  if (!storeDomain || !SHOPIFY_STOREFRONT_PUBLIC_ACCESS_TOKEN) return null;

  return `https://${storeDomain}/api/${SHOPIFY_STOREFRONT_API_VERSION}/graphql.json`;
};

export const fetchActiveGuestCampaign = async () => {
  const endpoint = getShopifyEndpoint();
  if (!endpoint) return null;

  const metaobjects = [];
  let after = null;
  let hasNextPage = true;

  try {
    while (hasNextPage) {
      const response = await fetch(endpoint, {
        method: "POST",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_PUBLIC_ACCESS_TOKEN,
        },
        body: JSON.stringify({
          query: CAMPAIGN_POPUPS_QUERY,
          variables: {
            type: CAMPAIGN_METAOBJECT_TYPE,
            first: CAMPAIGNS_PER_PAGE,
            after,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Shopify campaign request failed with status ${response.status}`);
      }

      const result = await response.json();
      if (result.errors?.length) {
        throw new Error(result.errors.map((error) => error.message).join("; "));
      }

      const connection = result.data?.metaobjects;
      metaobjects.push(...(connection?.nodes ?? []));
      hasNextPage = connection?.pageInfo?.hasNextPage ?? false;
      after = connection?.pageInfo?.endCursor ?? null;
    }

    return selectActiveGuestCampaign(metaobjects);
  } catch (error) {
    console.error("Unable to load the Shopify campaign popup.", error);
    return null;
  }
};
